import { useCallback, useMemo } from 'react';
import queryString from 'query-string';
import { useLocation, useHistory } from 'react-router-dom';

const useQueryString = <T>() => {
  const history = useHistory();
  const { search, pathname } = useLocation();
  const query = useMemo(() => queryString.parse(search), [search]);

  const onAddQueryString = useCallback(
    (value: Partial<T>) => {
      const newQuery = { ...query, ...value };
      history.push(`${pathname}?${queryString.stringify(newQuery)}`);
    },
    [query, history, pathname]
  );

  const onUpdateQueryString = useCallback(
    (value: Partial<T>) => {
      const newQuery = { ...query, ...value };
      history.replace(`${pathname}?${queryString.stringify(newQuery)}`);
    },
    [query, history, pathname]
  );

  const onReplaceQueryString = useCallback(
    (value: Partial<T>) => {
      const emptyQuery = queryString.parse('');
      const newQuery = { ...emptyQuery, ...value };
      history.replace(`${pathname}?${queryString.stringify(newQuery)}`);
    },
    [history, pathname]
  );

  const onMoveQueryString = useCallback(
    (value: Partial<T>) => {
      history.replace(`${pathname}?${queryString.stringify(value)}`);
    },
    [history, pathname]
  );

  const onPushNewQueryString = useCallback(
    (value: Partial<T>) => {
      history.push(`${pathname}?${queryString.stringify(value)}`);
    },
    [history, pathname]
  );

  const getQueryString = useCallback((value: Partial<T>) => {
    return queryString.stringify(value);
  }, []);

  return {
    query: (query as unknown) as T,
    onAddQueryString,
    onUpdateQueryString,
    onMoveQueryString,
    onReplaceQueryString,
    onPushNewQueryString,
    getQueryString
  };
};

export default useQueryString;
